import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import { Colors, Radius, Shadow, Typography } from '../theme';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/themes';
import type { TonyteActivity } from '../types';
import { useI18n } from '../i18n';
import { toLocalDateKey } from '../utils/activityDates';
import { getWeekRange, isInWeeklyFeaturedWindow } from '../utils/weeklyFeatured';
import {
  clearWeeklyFeatured,
  featuredPatch,
  parseFeaturedDateInput,
  resolvePartnerEligibility,
  setWeeklyFeatured,
  validateFeaturedSettings,
  type FeaturedSettings,
  type PartnerEligibility,
} from '../utils/weeklyFeaturedActions';

type Preset = 'this_week' | 'next_week' | 'custom';

type Props = {
  activity: TonyteActivity | null;
  adminUid: string;
  onClose: () => void;
  // Called with the patch that was written, for the optimistic feed-cache update.
  onSaved: (activity: TonyteActivity, patch: Partial<TonyteActivity>) => void;
};

const PRESETS: Array<{ id: Preset; labelKey: string }> = [
  { id: 'this_week', labelKey: 'featuredAdmin.thisWeek' },
  { id: 'next_week', labelKey: 'featuredAdmin.nextWeek' },
  { id: 'custom', labelKey: 'featuredAdmin.custom' },
];

const FeaturedEventModal: React.FC<Props> = ({ activity, adminUid, onClose, onSaved }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const themed = useMemo(() => createThemeStyles(theme), [theme]);

  const [preset, setPreset] = useState<Preset>('this_week');
  const [startText, setStartText] = useState('');
  const [endText, setEndText] = useState('');
  const [priorityText, setPriorityText] = useState('1');
  const [partner, setPartner] = useState<PartnerEligibility | 'checking'>('checking');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const currentlyFeatured = Boolean(activity && activity.isWeeklyFeatured === true);

  const applyPreset = (next: Preset) => {
    setPreset(next);
    if (next === 'custom') return;
    const range = getWeekRange(new Date(), next === 'next_week' ? 1 : 0);
    setStartText(toLocalDateKey(range.start));
    setEndText(toLocalDateKey(range.end));
  };

  // Reset whenever a different event is opened.
  useEffect(() => {
    if (!activity) return undefined;
    setError(null);
    setBusy(false);
    if (activity.isWeeklyFeatured && activity.featuredStartAt && activity.featuredEndAt) {
      setPreset('custom');
      setStartText(toLocalDateKey(new Date(activity.featuredStartAt)));
      setEndText(toLocalDateKey(new Date(activity.featuredEndAt)));
      setPriorityText(String(activity.featuredPriority ?? 1));
    } else {
      const range = getWeekRange(new Date(), 0);
      setPreset('this_week');
      setStartText(toLocalDateKey(range.start));
      setEndText(toLocalDateKey(range.end));
      setPriorityText('1');
    }
    let cancelled = false;
    setPartner('checking');
    resolvePartnerEligibility(activity).then(result => {
      if (!cancelled) setPartner(result);
    });
    return () => {
      cancelled = true;
    };
  }, [activity]);

  if (!activity) return null;

  const notVerified = partner === 'not_partner' || partner === 'unknown';

  const handleSave = async () => {
    const start = parseFeaturedDateInput(startText, false);
    const end = parseFeaturedDateInput(endText, true);
    const priority = Number(priorityText.trim());
    if (!start || !end) {
      setError(t('featuredAdmin.errors.invalidDate'));
      return;
    }
    const settings: FeaturedSettings = { start, end, priority };
    const invalid = validateFeaturedSettings(settings);
    if (invalid) {
      setError(t(`featuredAdmin.errors.${invalid}`));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await setWeeklyFeatured(activity, adminUid, settings);
      onSaved(activity, featuredPatch(settings, adminUid));
      onClose();
    } catch (saveError) {
      console.warn('[Tonyte][RN][Featured] save failed', { eventId: activity.id, error: saveError });
      setError(t('featuredAdmin.errors.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setError(null);
    setBusy(true);
    try {
      await clearWeeklyFeatured(activity, adminUid);
      onSaved(activity, featuredPatch(null, adminUid));
      onClose();
    } catch (removeError) {
      console.warn('[Tonyte][RN][Featured] remove failed', { eventId: activity.id, error: removeError });
      setError(t('featuredAdmin.errors.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <TouchableOpacity activeOpacity={1} style={styles.backdrop} onPress={onClose}>
        <TouchableOpacity activeOpacity={1} style={[styles.sheet, themed.sheet]}>
          <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
            <View style={styles.header}>
              <View style={styles.headerCopy}>
                <Text style={[styles.title, themed.textPrimary]}>{t('featuredAdmin.title')}</Text>
                <Text style={[styles.subtitle, themed.textMuted]} numberOfLines={2}>{activity.title}</Text>
              </View>
              <TouchableOpacity onPress={onClose} accessibilityRole="button" accessibilityLabel={t('common.cancel')}>
                <Icon name="x" size={18} color={theme.colors.icon} />
              </TouchableOpacity>
            </View>

            <View style={[styles.partnerRow, notVerified ? styles.partnerRowWarning : styles.partnerRowOk, notVerified ? null : themed.partnerRowOk]}>
              {partner === 'checking' ? (
                <ActivityIndicator size="small" color={theme.colors.accentSecondary} />
              ) : (
                <Icon
                  name={notVerified ? 'alert-triangle' : 'check-circle'}
                  size={15}
                  color={notVerified ? Colors.sunset : theme.colors.accentSecondary}
                />
              )}
              <Text style={[styles.partnerText, themed.textSecondary]}>
                {partner === 'checking'
                  ? t('featuredAdmin.checkingPartner')
                  : partner === 'partner'
                    ? t('featuredAdmin.verifiedPartner')
                    : partner === 'unknown'
                      ? t('featuredAdmin.unknownPartner')
                      : t('featuredAdmin.notPartner')}
              </Text>
            </View>

            <View style={styles.presetRow}>
              {PRESETS.map(item => {
                const active = preset === item.id;
                return (
                  <TouchableOpacity
                    key={item.id}
                    activeOpacity={0.84}
                    onPress={() => applyPreset(item.id)}
                    style={[styles.presetChip, themed.chip, active && styles.presetChipActive]}
                    accessibilityRole="button"
                    accessibilityState={{ selected: active }}
                  >
                    <Text style={[styles.presetText, themed.textSecondary, active && styles.presetTextActive]}>
                      {t(item.labelKey)}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            <View style={styles.fieldRow}>
              <View style={styles.field}>
                <Text style={[styles.label, themed.textMuted]}>{t('featuredAdmin.startDate')}</Text>
                <TextInput
                  value={startText}
                  onChangeText={value => { setStartText(value); setPreset('custom'); }}
                  placeholder="YYYY-MM-DD"
                  placeholderTextColor={theme.colors.textPlaceholder}
                  autoCapitalize="none"
                  autoCorrect={false}
                  style={[styles.input, themed.input]}
                />
              </View>
              <View style={styles.field}>
                <Text style={[styles.label, themed.textMuted]}>{t('featuredAdmin.endDate')}</Text>
                <TextInput
                  value={endText}
                  onChangeText={value => { setEndText(value); setPreset('custom'); }}
                  placeholder="YYYY-MM-DD"
                  placeholderTextColor={theme.colors.textPlaceholder}
                  autoCapitalize="none"
                  autoCorrect={false}
                  style={[styles.input, themed.input]}
                />
              </View>
            </View>

            <View style={styles.field}>
              <Text style={[styles.label, themed.textMuted]}>{t('featuredAdmin.priority')}</Text>
              <TextInput
                value={priorityText}
                onChangeText={setPriorityText}
                keyboardType="number-pad"
                placeholder="1"
                placeholderTextColor={theme.colors.textPlaceholder}
                style={[styles.input, styles.priorityInput, themed.input]}
              />
              <Text style={[styles.hint, themed.textMuted]}>{t('featuredAdmin.priorityHint')}</Text>
            </View>

            {currentlyFeatured && isInWeeklyFeaturedWindow(activity) ? (
              <Text style={[styles.hint, themed.textMuted]}>{t('featuredAdmin.currentlyFeatured')}</Text>
            ) : null}

            {error ? <Text style={styles.error}>{error}</Text> : null}

            <View style={styles.actions}>
              {currentlyFeatured ? (
                <TouchableOpacity
                  activeOpacity={0.84}
                  disabled={busy}
                  onPress={handleRemove}
                  style={[styles.button, styles.buttonDanger]}
                  accessibilityRole="button"
                >
                  <Text style={styles.buttonDangerText}>{t('featuredAdmin.remove')}</Text>
                </TouchableOpacity>
              ) : null}
              <TouchableOpacity
                activeOpacity={0.84}
                disabled={busy || partner === 'checking'}
                onPress={handleSave}
                style={[styles.button, styles.buttonPrimary, (busy || partner === 'checking') && styles.buttonDisabled]}
                accessibilityRole="button"
              >
                {busy ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.buttonPrimaryText}>
                    {notVerified ? t('featuredAdmin.saveAnyway') : t('featuredAdmin.save')}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </ScrollView>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.58)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  sheet: {
    width: '100%',
    maxWidth: 440,
    maxHeight: '86%',
    borderRadius: 18,
    borderWidth: 1,
    padding: 16,
    // Midnight (dark theme) colors — the canonical implementation, same
    // convention as every other themed component in this app (see
    // daylightStyle.ts): createThemeStyles below only ever overrides these
    // for the daylight theme, so midnight must never rely on it for color.
    backgroundColor: '#161B26',
    borderColor: 'rgba(255,255,255,0.1)',
    ...Shadow.strong,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12,
  },
  headerCopy: { flex: 1 },
  title: { color: '#ffffff', fontSize: 16, lineHeight: 21, fontWeight: Typography.extrabold },
  subtitle: { color: 'rgba(255,255,255,0.4)', marginTop: 2, fontSize: 12, lineHeight: 16, fontWeight: Typography.medium },
  partnerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    padding: 11,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 14,
  },
  partnerRowWarning: {
    borderColor: 'rgba(242,125,38,0.5)',
    backgroundColor: 'rgba(242,125,38,0.1)',
  },
  partnerRowOk: {
    backgroundColor: 'rgba(38,208,206,0.07)',
    borderColor: 'rgba(38,208,206,0.22)',
  },
  partnerText: { color: 'rgba(255,255,255,0.65)', flex: 1, fontSize: 12, lineHeight: 16, fontWeight: Typography.medium },
  presetRow: { flexDirection: 'row', gap: 8, marginBottom: 14, flexWrap: 'wrap' },
  presetChip: {
    height: 32,
    paddingHorizontal: 12,
    borderRadius: 16,
    borderWidth: 1,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderColor: 'rgba(255,255,255,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  presetChipActive: {
    backgroundColor: 'rgba(242,125,38,0.20)',
    borderColor: 'rgba(242,125,38,0.62)',
  },
  presetText: { color: 'rgba(255,255,255,0.65)', fontSize: 12, fontWeight: Typography.bold },
  presetTextActive: { color: Colors.sunset },
  fieldRow: { flexDirection: 'row', gap: 10 },
  field: { flex: 1, marginBottom: 12 },
  label: { color: 'rgba(255,255,255,0.4)', fontSize: 11, lineHeight: 14, fontWeight: Typography.bold, marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.6 },
  input: {
    height: 40,
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 12,
    fontSize: 14,
    fontWeight: Typography.medium,
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderColor: 'rgba(255,255,255,0.1)',
    color: '#ffffff',
  },
  priorityInput: { maxWidth: 110 },
  hint: { color: 'rgba(255,255,255,0.4)', marginTop: 5, fontSize: 11, lineHeight: 15, fontWeight: Typography.medium },
  error: { color: Colors.error, fontSize: 12, lineHeight: 16, fontWeight: Typography.semibold, marginTop: 8 },
  actions: { flexDirection: 'row', gap: 10, marginTop: 16, justifyContent: 'flex-end' },
  button: {
    minHeight: 42,
    paddingHorizontal: 16,
    borderRadius: Radius.pill,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonPrimary: { backgroundColor: Colors.sunset, flexGrow: 1 },
  buttonPrimaryText: { color: '#fff', fontSize: 13, fontWeight: Typography.extrabold },
  buttonDanger: { borderWidth: 1, borderColor: 'rgba(239,68,68,0.6)', backgroundColor: 'rgba(239,68,68,0.12)' },
  buttonDangerText: { color: '#ef4444', fontSize: 13, fontWeight: Typography.bold },
  buttonDisabled: { opacity: 0.6 },
});

const createThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  sheet: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.border,
    ...theme.shadows.elevated,
  }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
  textMuted: daylightStyle(theme, { color: theme.colors.textMuted }),
  chip: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackground,
    borderColor: theme.colors.border,
  }),
  input: daylightStyle(theme, {
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.border,
    color: theme.colors.textPrimary,
  }),
  partnerRowOk: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    borderColor: theme.colors.accentSecondaryBorder,
  }),
});

export default FeaturedEventModal;
